import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import {
  Moon, Plus, Edit, Trash2, Loader2, ToggleLeft, ToggleRight,
  Sunrise, RefreshCw,
} from 'lucide-react';
import { parseUtc } from '../../lib/parseUtc';

/**
 * Sleep Schedule — schedule-based scale-to-zero with automatic wake.
 *
 * Distinct from the activity-based Serverless page: this is *time-driven*.
 * Operators define cron windows (e.g. "weeknights 19:00 → 07:00") and the
 * backend sleep_scheduler scales matching workloads down to
 * `sleep_replicas` at the downscale time and restores them at the restore
 * time. The activity-based Serverless feature scales to zero on *traffic
 * idleness* and wakes on the next request — different trigger entirely.
 *
 * Backend (sleep_scheduler.py — already wired + running) reads:
 *   - cluster opt-in: clusters.node_optimization_config.sleep_mode_enabled
 *   - schedules:      db.sleep_policies docs with schedule_enabled=true
 * and emits replica_scale actions the agent executes.
 *
 * REST (mounted under /api via node_optimization_router):
 *   GET    /v1/clusters/{cid}/sleep/config
 *   POST   /v1/clusters/{cid}/sleep/mode      {enabled}
 *   GET    /v1/clusters/{cid}/sleep/policies
 *   POST   /v1/clusters/{cid}/sleep/policies  <policy fields>
 *   PUT    /v1/clusters/{cid}/sleep/policies/{policy_id}
 *   DELETE /v1/clusters/{cid}/sleep/policies/{policy_id}
 *   GET    /v1/clusters/{cid}/sleep/status
 */

// Friendly presets that fill the cron fields. Operators can still type raw
// cron — these are just one-click starting points for the common cases.
const PRESETS = [
  {
    key: 'weeknights',
    label: 'Weeknights 19:00 → 07:00',
    downscale_cron: '0 19 * * 1-5',
    restore_cron: '0 7 * * 1-5',
  },
  {
    key: 'weekends',
    label: 'Weekends off (Fri 19:00 → Mon 07:00)',
    downscale_cron: '0 19 * * 5',
    restore_cron: '0 7 * * 1',
  },
  {
    key: 'nightly',
    label: 'Nightly 20:00 → 08:00 (every day)',
    downscale_cron: '0 20 * * *',
    restore_cron: '0 8 * * *',
  },
];

const empty = {
  name: '',
  namespace_selector: '',
  downscale_cron: '0 19 * * 1-5',
  restore_cron: '0 7 * * 1-5',
  schedule_timezone: '',
  sleep_replicas: 0,
  min_replicas: 1,
  mode: 'Manual',
  schedule_enabled: true,
};

const PolicyModal = ({ open, onClose, clusterId, policy, onSaved }) => {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const isEdit = !!policy?.policy_id;

  useEffect(() => {
    if (!open) return;
    setError(null);
    if (policy) {
      setForm({
        name: policy.name || '',
        namespace_selector: policy.namespace_selector || '',
        downscale_cron: policy.downscale_cron || '0 19 * * 1-5',
        restore_cron: policy.restore_cron || '0 7 * * 1-5',
        schedule_timezone: policy.schedule_timezone || '',
        sleep_replicas: policy.sleep_replicas ?? 0,
        min_replicas: policy.min_replicas ?? 1,
        mode: policy.mode === 'Auto' ? 'Auto' : 'Manual',
        schedule_enabled: policy.schedule_enabled !== false,
      });
    } else {
      setForm(empty);
    }
  }, [open, policy]);

  const update = (k) => (e) => {
    const v = e?.target?.type === 'checkbox' ? e.target.checked : e?.target?.value ?? e;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const applyPreset = (p) => {
    setForm((f) => ({ ...f, downscale_cron: p.downscale_cron, restore_cron: p.restore_cron }));
  };

  const submit = async () => {
    // Client-side validation: crons are load-bearing for the scheduler.
    if (!form.downscale_cron.trim() || !form.restore_cron.trim()) {
      setError('Both sleep and wake cron expressions are required.');
      return;
    }
    if (!form.name.trim()) {
      setError('Name is required.');
      return;
    }
    setSaving(true);
    setError(null);
    const payload = {
      name: form.name.trim(),
      namespace_selector: form.namespace_selector.trim(),
      downscale_cron: form.downscale_cron.trim(),
      restore_cron: form.restore_cron.trim(),
      schedule_timezone: form.schedule_timezone.trim(),
      sleep_replicas: Math.max(0, Number(form.sleep_replicas) || 0),
      min_replicas: Math.max(1, Number(form.min_replicas) || 1),
      mode: form.mode === 'Auto' ? 'Auto' : 'Manual',
      schedule_enabled: !!form.schedule_enabled,
    };
    try {
      if (isEdit) {
        await axios.put(
          `${API}/v1/clusters/${clusterId}/sleep/policies/${policy.policy_id}`,
          payload,
        );
      } else {
        await axios.post(
          `${API}/v1/clusters/${clusterId}/sleep/policies`,
          payload,
        );
      }
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? 'Edit sleep schedule' : 'New sleep schedule'}
          </DialogTitle>
        </DialogHeader>

        {/* Friendly presets — fill the cron fields, raw entry still allowed */}
        <div className="flex flex-wrap gap-2">
          <span className="text-xs text-slate-500 self-center mr-1">Presets:</span>
          {PRESETS.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => applyPreset(p)}
              className="px-2 py-1 text-xs rounded border border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
            >
              {p.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <Label className="text-xs">Display name</Label>
            <Input value={form.name} onChange={update('name')} placeholder="weeknights-dev" />
          </div>
          <div>
            <Label className="text-xs" title="Glob pattern — leave blank to match all namespaces">Namespaces</Label>
            <Input
              value={form.namespace_selector}
              onChange={update('namespace_selector')}
              placeholder="dev-* or staging"
            />
          </div>
          <div>
            <Label className="text-xs" title="5-field cron expression">Sleep at (cron)</Label>
            <Input value={form.downscale_cron} onChange={update('downscale_cron')} placeholder="0 19 * * 1-5" />
          </div>
          <div>
            <Label className="text-xs" title="5-field cron expression">Wake at (cron)</Label>
            <Input value={form.restore_cron} onChange={update('restore_cron')} placeholder="0 7 * * 1-5" />
          </div>
          <div>
            <Label className="text-xs" title="IANA timezone name — leave blank for UTC">Timezone</Label>
            <Input
              value={form.schedule_timezone}
              onChange={update('schedule_timezone')}
              placeholder="UTC or America/New_York"
            />
          </div>
          <div>
            <Label className="text-xs">Approval mode</Label>
            <select
              className="w-full h-9 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300"
              value={form.mode}
              onChange={update('mode')}
            >
              <option value="Manual">Manual — review before applying</option>
              <option value="Auto">Automatic — apply on schedule</option>
            </select>
          </div>
          <div>
            <Label className="text-xs">Replicas while asleep</Label>
            <Input
              type="number"
              min="0"
              value={form.sleep_replicas}
              onChange={update('sleep_replicas')}
            />
          </div>
          <div>
            <Label className="text-xs">Minimum replicas on wake</Label>
            <Input
              type="number"
              min="1"
              value={form.min_replicas}
              onChange={update('min_replicas')}
            />
          </div>

          <div className="col-span-2 flex items-center gap-2">
            <Checkbox
              id="sleep_enabled"
              checked={form.schedule_enabled}
              onCheckedChange={update('schedule_enabled')}
            />
            <Label htmlFor="sleep_enabled" className="text-xs cursor-pointer">
              Schedule enabled
            </Label>
          </div>
        </div>
        {error && <div className="text-xs text-red-600">{error}</div>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="h-3 w-3 mr-1 animate-spin" /> Saving…
              </>
            ) : isEdit ? (
              'Save changes'
            ) : (
              'Create schedule'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const fmtTime = (iso) => {
  if (!iso) return '—';
  try {
    return parseUtc(iso).toLocaleString();
  } catch (_) {
    return String(iso);
  }
};

// Render-only: turn simple 5-field crons ("0 19 * * 1-5") into a human time
// ("7:00 PM Mon–Fri"). Returns null for anything fancier — caller falls back
// to showing the raw cron.
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const humanizeCron = (cron) => {
  const parts = String(cron || '').trim().split(/\s+/);
  if (parts.length !== 5) return null;
  const [min, hour, dom, mon, dow] = parts;
  if (!/^\d{1,2}$/.test(min) || !/^\d{1,2}$/.test(hour) || dom !== '*' || mon !== '*') return null;
  const h = Number(hour);
  const m = Number(min);
  if (h > 23 || m > 59) return null;
  const time = `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
  let days;
  if (dow === '*') {
    days = 'daily';
  } else if (/^[0-6]$/.test(dow)) {
    days = DOW[Number(dow)];
  } else if (/^[0-6]-[0-6]$/.test(dow)) {
    const [a, b] = dow.split('-').map(Number);
    days = `${DOW[a]}–${DOW[b]}`;
  } else {
    return null;
  }
  return `${time} ${days}`;
};

const SleepSchedule = ({ clusterId }) => {
  const [sleepModeEnabled, setSleepModeEnabled] = useState(false);
  const [policies, setPolicies] = useState([]);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [togglingMode, setTogglingMode] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // Config (master switch + policies) — the primary source of truth.
  const fetchConfig = useCallback(async () => {
    if (!clusterId) return;
    try {
      const res = await axios.get(`${API}/v1/clusters/${clusterId}/sleep/config`);
      setSleepModeEnabled(!!res.data?.sleep_mode_enabled);
      setPolicies(Array.isArray(res.data?.policies) ? res.data.policies : []);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  }, [clusterId]);

  // Status (slept workloads + next sleep/wake) — best-effort, not load-bearing.
  const fetchStatus = useCallback(async () => {
    if (!clusterId) return;
    try {
      const res = await axios.get(`${API}/v1/clusters/${clusterId}/sleep/status`);
      setStatus(res.data || null);
    } catch (_) {
      setStatus(null);
    }
  }, [clusterId]);

  useEffect(() => {
    fetchConfig();
    fetchStatus();
    // 60s refresh so background scheduler runs (sleep/wake transitions)
    // surface here without a manual reload.
    const id = setInterval(() => {
      fetchConfig();
      fetchStatus();
    }, 60000);
    return () => clearInterval(id);
  }, [fetchConfig, fetchStatus]);

  const handleModeToggle = async () => {
    setTogglingMode(true);
    const next = !sleepModeEnabled;
    try {
      const res = await axios.post(`${API}/v1/clusters/${clusterId}/sleep/mode`, {
        enabled: next,
      });
      setSleepModeEnabled(!!res.data?.sleep_mode_enabled);
      fetchStatus();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setTogglingMode(false);
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete sleep schedule "${p.name}"?`)) return;
    setDeletingId(p.policy_id);
    try {
      await axios.delete(`${API}/v1/clusters/${clusterId}/sleep/policies/${p.policy_id}`);
      await fetchConfig();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setDeletingId(null);
    }
  };

  if (!clusterId) return null;

  // Map next sleep/wake from status by policy_id for inline display.
  const statusByPolicy = {};
  (status?.policies || []).forEach((s) => {
    if (s?.policy_id) statusByPolicy[s.policy_id] = s;
  });
  const sleptWorkloads = status?.slept_workloads || [];

  return (
    <Card className="mb-4">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle
          className="text-sm flex items-center gap-2"
          title="Time-driven schedules. Different from Serverless, which scales to zero on traffic idleness and wakes on the next request."
        >
          <Moon className="h-4 w-4 text-emerald-600" />
          Sleep Schedule
          <Badge className="bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400">
            {policies.length} schedule{policies.length === 1 ? '' : 's'}
          </Badge>
        </CardTitle>
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            setEditing(null);
            setModalOpen(true);
          }}
        >
          <Plus className="h-3 w-3 mr-1" />
          New schedule
        </Button>
      </CardHeader>
      <CardContent>
        <p className="text-xs text-slate-500 mb-3">
          Scale selected workloads down on a schedule and wake them automatically.
        </p>

        {error && <p className="text-xs text-red-600 mb-2">{error}</p>}

        {/* Master toggle — cluster opt-in (sleep_mode_enabled). */}
        <div
          className={`flex items-center justify-between p-3 rounded-lg mb-4 ${
            sleepModeEnabled
              ? 'bg-emerald-50 border border-emerald-200 dark:bg-emerald-950/40 dark:border-emerald-900'
              : 'bg-slate-50 border border-slate-200 dark:bg-slate-800/50 dark:border-slate-700'
          }`}
        >
          <div className="flex items-center gap-2">
            <Moon className={`h-4 w-4 ${sleepModeEnabled ? 'text-emerald-600' : 'text-slate-500'}`} />
            <div>
              <span className={`text-sm font-medium ${sleepModeEnabled ? 'text-emerald-800 dark:text-emerald-300' : 'text-slate-700 dark:text-slate-300'}`}>
                Sleep Mode
              </span>
              <p className="text-xs text-slate-500">
                {sleepModeEnabled
                  ? 'Schedules run automatically for this cluster.'
                  : 'Turn on to let schedules run for this cluster.'}
              </p>
            </div>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={handleModeToggle}
            disabled={togglingMode}
            className="flex items-center gap-1"
          >
            {togglingMode ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : sleepModeEnabled ? (
              <ToggleRight className="h-5 w-5 text-emerald-600" />
            ) : (
              <ToggleLeft className="h-5 w-5 text-slate-400" />
            )}
            {sleepModeEnabled ? 'Enabled' : 'Disabled'}
          </Button>
        </div>

        {/* Policy list */}
        {policies.length === 0 ? (
          <p className="text-xs text-slate-500">
            No sleep schedules yet. Create one to scale dev or staging workloads
            down outside working hours.
          </p>
        ) : (
          <div className="border rounded-md overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 dark:bg-slate-800/50">
                <tr>
                  <th className="text-left px-2 py-1.5 text-[11px] uppercase tracking-wide text-slate-400 font-medium">Name</th>
                  <th className="text-left px-2 py-1.5 text-[11px] uppercase tracking-wide text-slate-400 font-medium">Namespace</th>
                  <th className="text-left px-2 py-1.5 text-[11px] uppercase tracking-wide text-slate-400 font-medium">Sleeps at</th>
                  <th className="text-left px-2 py-1.5 text-[11px] uppercase tracking-wide text-slate-400 font-medium">Wakes at</th>
                  <th className="text-right px-2 py-1.5 text-[11px] uppercase tracking-wide text-slate-400 font-medium">Replicas asleep</th>
                  <th className="text-left px-2 py-1.5 text-[11px] uppercase tracking-wide text-slate-400 font-medium">Mode</th>
                  <th className="text-left px-2 py-1.5 text-[11px] uppercase tracking-wide text-slate-400 font-medium">Next</th>
                  <th className="px-2 py-1.5" />
                </tr>
              </thead>
              <tbody>
                {policies.map((p) => {
                  const st = statusByPolicy[p.policy_id];
                  return (
                    <tr key={p.policy_id} className="border-t hover:bg-slate-50 dark:hover:bg-slate-800/50">
                      <td className="px-2 py-1.5 font-medium">
                        {p.name}
                        {p.schedule_enabled === false && (
                          <Badge className="ml-1 rounded-full px-2 py-0.5 bg-slate-100 text-slate-500 text-[10px]">
                            Paused
                          </Badge>
                        )}
                      </td>
                      <td className="px-2 py-1.5">
                        <span className="font-mono text-xs text-slate-600 dark:text-slate-400">{p.namespace_selector || '* (all)'}</span>
                      </td>
                      <td className="px-2 py-1.5" title={p.downscale_cron}>
                        {humanizeCron(p.downscale_cron) || (
                          <span className="font-mono text-xs text-slate-600 dark:text-slate-400">{p.downscale_cron}</span>
                        )}
                      </td>
                      <td className="px-2 py-1.5" title={p.restore_cron}>
                        {humanizeCron(p.restore_cron) || (
                          <span className="font-mono text-xs text-slate-600 dark:text-slate-400">{p.restore_cron}</span>
                        )}
                        <span className="text-[10px] text-slate-500 ml-1">
                          {p.schedule_timezone || 'UTC'}
                        </span>
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{p.sleep_replicas ?? 0}</td>
                      <td className="px-2 py-1.5">
                        <Badge
                          className={
                            p.mode === 'Auto'
                              ? 'rounded-full px-2 py-0.5 bg-emerald-50 text-emerald-700 text-[10px]'
                              : 'rounded-full px-2 py-0.5 bg-slate-100 text-slate-600 text-[10px]'
                          }
                        >
                          {p.mode === 'Auto' ? 'Auto' : 'Manual'}
                        </Badge>
                      </td>
                      <td className="px-2 py-1.5 text-[10px] text-slate-500">
                        {st ? (
                          <>
                            <div className="flex items-center gap-1">
                              <Moon className="h-3 w-3 text-slate-400" />
                              {fmtTime(st.next_downscale)}
                            </div>
                            <div className="flex items-center gap-1">
                              <Sunrise className="h-3 w-3 text-amber-400" />
                              {fmtTime(st.next_restore)}
                            </div>
                          </>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-right whitespace-nowrap">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setEditing(p);
                            setModalOpen(true);
                          }}
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(p)}
                          disabled={deletingId === p.policy_id}
                        >
                          {deletingId === p.policy_id ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Trash2 className="h-3 w-3 text-red-500" />
                          )}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Currently slept workloads from /sleep/status */}
        {sleptWorkloads.length > 0 && (
          <div className="mt-4">
            <div className="flex items-center gap-2 mb-2">
              <Moon className="h-4 w-4 text-emerald-600" />
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                Currently sleeping ({sleptWorkloads.length})
              </p>
            </div>
            <div className="border rounded-md overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 dark:bg-slate-800/50">
                  <tr>
                    <th className="text-left px-2 py-1.5 text-[11px] uppercase tracking-wide text-slate-400 font-medium">Workload</th>
                    <th className="text-right px-2 py-1.5 text-[11px] uppercase tracking-wide text-slate-400 font-medium">Original replicas</th>
                    <th className="text-left px-2 py-1.5 text-[11px] uppercase tracking-wide text-slate-400 font-medium">Since</th>
                  </tr>
                </thead>
                <tbody>
                  {sleptWorkloads.map((w, i) => (
                    <tr key={`${w.namespace}/${w.workload_name}/${i}`} className="border-t">
                      <td className="px-2 py-1.5 font-mono text-xs text-slate-600 dark:text-slate-400">
                        {w.namespace}/{w.workload_name}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{w.original_replicas ?? '—'}</td>
                      <td className="px-2 py-1.5 text-slate-600 dark:text-slate-400">{fmtTime(w.since)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>

      <PolicyModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        clusterId={clusterId}
        policy={editing}
        onSaved={() => {
          fetchConfig();
          fetchStatus();
        }}
      />
    </Card>
  );
};

export default SleepSchedule;
